import { type FC } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';
import {
    TRADE_WELCOME_PROMO_CODE,
    TRADE_FIRST_WELCOME_PROMO_CODE,
} from 'dibs-buyer-layout/exports/tradePromoConstants';

import { PromoCodeLoader } from '../../global/PromoCodeLoader';
import { TradePromoLoader } from '../../global/TradePromo/TradePromoLoader';

import dibsCss from 'dibs-css';

import { type PdpSharedPromoCode_user$key } from './__generated__/PdpSharedPromoCode_user.graphql';
import { type PdpSharedPromoCode_displayInfo$key } from './__generated__/PdpSharedPromoCode_displayInfo.graphql';
import { type PdpSharedPromoCode_viewer$key } from './__generated__/PdpSharedPromoCode_viewer.graphql';

type Props = {
    user?: PdpSharedPromoCode_user$key | null;
    userId?: string | null;
    item?: object;
    itemId?: string;
    displayInfo?: PdpSharedPromoCode_displayInfo$key | null;
    viewer: PdpSharedPromoCode_viewer$key;
    onlyShippingPromos?: boolean;
    excludeShippingPromos: boolean;
    currency: string;
    containerClass?: string;
};

const viewerFragment = graphql`
    fragment PdpSharedPromoCode_viewer on Viewer {
        activePromotion(userId: $userId, itemId: $itemId) {
            code
        }
    }
`;

const userFragment = graphql`
    fragment PdpSharedPromoCode_user on User {
        ...TradePromo_user @include(if: $isTrade)
        tradeFirm @include(if: $isTrade) {
            rewards {
                currentPromoSegment(userId: $userId) {
                    segmentCode
                }
            }
        }
    }
`;

const displayInfoFragment = graphql`
    fragment PdpSharedPromoCode_displayInfo on ItemDisplayInfo {
        pricing {
            showSale
            showNet
        }
    }
`;

export const PdpSharedPromoCode: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    itemId,
    displayInfo: displayInfoRef,
    onlyShippingPromos,
    excludeShippingPromos,
    currency,
    containerClass = '',
}) => {
    const environment = useRelayEnvironment();

    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const displayInfo = useFragment(displayInfoFragment, displayInfoRef);

    if (!displayInfo && !onlyShippingPromos) {
        return null;
    }

    const tradeFirm = user?.tradeFirm;
    const userLevelPromo = viewer?.activePromotion;
    const userLevelPromoCode = userLevelPromo?.code;
    const hasFirmLevelPromo = tradeFirm?.rewards?.currentPromoSegment?.segmentCode;
    const hasTradeUserPromo = [TRADE_WELCOME_PROMO_CODE, TRADE_FIRST_WELCOME_PROMO_CODE].includes(
        userLevelPromoCode || ''
    );

    // if there is no user-level promo, render this for trade users so points-earned messaging appears
    if (hasTradeUserPromo || (tradeFirm && (hasFirmLevelPromo || !userLevelPromo))) {
        return <TradePromoLoader user={user} />;
    }

    const { showSale: showSalePrice, showNet: showNetPrice } = displayInfo?.pricing || {};

    const containerClasses = classnames(
        containerClass,
        dibsCss.textMarketingsale,
        onlyShippingPromos ? dibsCss.mb0 : dibsCss.mbXsmall
    );

    return (
        <PromoCodeLoader
            load
            environment={environment}
            itemId={itemId}
            isFurtherReduced={!!(showSalePrice || showNetPrice)}
            containerClass={containerClasses}
            displaySingleLine
            onlyShippingPromos={onlyShippingPromos}
            excludeShippingPromos={excludeShippingPromos}
            currency={currency}
        />
    );
};
